import React from "react";
import { Container, Skeleton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { columns as allColumns } from './clientsHeaders.js';
import { formatCurrency } from '../../../utils/Format.js';
import styles from "./clientsTableStyles.js";
import NoData from "../../SharedComponents/NoData/index.js";
import { usePermissions } from "../../../hooks/permissions/usePermissions.js";

const ClientsTable = ({ users = [], isLoading, search }) => {
  const navigate = useNavigate();

  const { hasPermission } = usePermissions();

  if (!hasPermission("cli_vw")) {
    return (
      <Container sx={styles.mainContainer}>
        <NoData title="No tienes permisos para ver clientes" />
      </Container>
    );
  }

  const filteredRows = users.filter((client) => {
    return Object.values(client).some((value) =>
      value.toString().toLowerCase().includes(search.toLowerCase())
    );
  });

  const columns = allColumns.filter(column => {
    if (column.field === 'credits' && !hasPermission('cli_vwc')) return false;
    if (column.field === 'balance' && !hasPermission('cli_vwb')) return false;
    return true;
  });

  const rows = filteredRows?.map((client, index) => ({
    id: client.id_user,
    user_name: `${client.user_name} ${client.paternal_lastname} ${client.maternal_lastname}`,
    status: client.trust_business === 1 ? "Confianza" : "No confianza",
    statusAccount: client.account_status,
    type: client.user_state === 1 ? "Activo" : "Inactivo",
    business_name: client.business_name === '' ? 'Sin negocio' : client.business_name,
    credits: formatCurrency(client.credits),
    balance: formatCurrency(client.balance),
    membership: client.membership_name ?? 'Sin categoría',
  }));

  const handleRowClick = (params) => {
    // Check if user has at least one of the required permissions
    if (hasPermission("cli_vwc") || hasPermission("cli_vwb") || hasPermission("cli_ed_sett")) {
      navigate(`/clientes/${params.id}`);
    }
  };

  return (
    <Container sx={styles.mainContainer}>
      {isLoading ? (
        <Container sx={styles.skeletonMainContainer}>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={'5%'} height={40} />
            <Skeleton variant="rounded" width={'30%'} height={40} />
            <Skeleton variant="rounded" width={'20%'} height={40} />
            <Skeleton variant="rounded" width={'20%'} height={40} />
            <Skeleton variant="rounded" width={'20%'} height={40} />
            <Skeleton variant="rounded" width={'20%'} height={40} />
          </Container>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={'5%'} height={300} />
            <Skeleton variant="rounded" width={'30%'} height={300} />
            <Skeleton variant="rounded" width={'20%'} height={300} />
            <Skeleton variant="rounded" width={'20%'} height={300} />
            <Skeleton variant="rounded" width={'20%'} height={300} />
            <Skeleton variant="rounded" width={'20%'} height={300} />
          </Container>
        </Container>
      ) : (
        <DataGrid
          autoHeight
          disableColumnFilter
          rows={rows}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          getRowHeight={() => 'auto'}
          sx={{
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          disableColumnMenu={true}
          disableSelectionOnClick={true}
          localeText={{
            noRowsLabel: <NoData title="No se encontraron usuarios" />,
          }}
          onRowClick={handleRowClick}
        />
      )}
    </Container>
  );
};

export default ClientsTable;
